import { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAiLearningList, useAiModuleList, useDeleteAiModule } from '../../hooks/queries/aiQueries';
import AiLearningTableRow from './components/AiLearningTableRow';
import AiLearningRequestModal from './components/AiLearningRequestModal';
import AiModuleDeleteModal from './components/AiModuleDeleteModal';
import AiLearningDemandReRequestModal from './components/AiLearningDemandReRequestModal';
import AiLearningCblReRequestModal from './components/AiLearningCblReRequestModal';
import Pagination from '../../components/Pagination/Pagination';
import { SkeletonTable } from '../../components/Skeleton';
import PlusIcon from '../../assets/icons/plus.svg?react';
import DeleteIcon from '../../assets/icons/delete.svg?react';
import RefreshIcon from '../../assets/icons/refresh.svg?react';
import dayjs from 'dayjs';
import './AiModuleManagement.css';

const AiModuleManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // 탭 정보 추출 (td 파라미터에서)
  const activeTab = searchParams.get('td') || 'learning';
  const keyword = searchParams.get('Keyword') || '';
  useEffect(() => {
    if (keyword) {
      setKeywordInput(keyword);
    } else {
      setKeywordInput('');
    }
  }, [keyword]);
  // 이전 탭 값을 추적하기 위한 ref
  const prevTabRef = useRef(activeTab);
  
  // updateSearchParams 함수 정의 (탭 변경 감지 useEffect보다 먼저)
  const updateSearchParams = (newParams) => {
    const current = Object.fromEntries(searchParams.entries());
    setSearchParams({ ...current, ...newParams });
  };
  
  const [isRequestModalOpen, setIsRequestModalOpen] = useState(false);
  const [isDemandReRequestModalOpen, setIsDemandReRequestModalOpen] = useState(false);
  const [isCblReRequestModalOpen, setIsCblReRequestModalOpen] = useState(false);
  const [isModuleDeleteModalOpen, setIsModuleDeleteModalOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [refreshMsg, setRefreshMsg] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  // URL 파라미터에서 상태 가져오기
  const currentPage = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('pageSize')) || 10;
  const sortBy = searchParams.get('sortBy') || 'RequestedAt';
  const sortOrder = searchParams.get('sortOrder') || 'desc';
  
  // 필터 파라미터들
  const baseAlgorithm = searchParams.get('BaseAlgorithm') || '';
  const aiModelType = searchParams.get('AiModelType') || '';
  const status = searchParams.get('Status') || '';
  const version = searchParams.get('Version') || '';
  
  // AI 모듈 필터 파라미터
  const moduleName = searchParams.get('ModuleName') || '';

  // 쿼리 파라미터 메모이제이션 (URL 상태와 연동)
  const aiLearningParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder,
    };
    
    if (keyword) params.Keyword = keyword;
    if (baseAlgorithm) params.BaseAlgorithm = baseAlgorithm;
    if (aiModelType) params.AiModelType = aiModelType;
    if (status) params.Status = status;
    if (version) params.Version = version;
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, keyword, baseAlgorithm, aiModelType, status, version]);

  const aiModuleParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder,
      AiModelType: 'ChargingDemandPrediction',

    };
    
    if (keyword) params.Keyword = keyword;
    if (moduleName) params.ModuleName = moduleName;
    if (version) params.Version = version;
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, keyword, moduleName, version]);


  const cblPredictionModuleParams = useMemo(() => {
    const params = {
      Page: currentPage,
      PageSize: pageSize,
      OrderBy: sortBy,
      OrderDirection: sortOrder,
      AiModelType: 'CblPrediction',
    };
    
    if (keyword) params.Keyword = keyword;
    if (moduleName) params.ModuleName = moduleName;
    if (version) params.Version = version;
    
    return params;
  }, [currentPage, pageSize, sortBy, sortOrder, keyword, moduleName, version]);

  // API 호출 (activeTab에 따라 조건부 실행)
  const { 
    data: aiLearningData, 
    isLoading: isLoadingLearning,
    error: errorLearning,
    refetch: refetchAiLearning
  } = useAiLearningList(aiLearningParams, {
    enabled: activeTab === 'learning'
  });

  const { 
    data: aiModuleData, 
    isLoading: isLoadingModule,
    error: errorModule,
    refetch: refetchAiModule
  } = useAiModuleList(aiModuleParams, {
    enabled: activeTab === 'demand-module'
  });

  const { 
    data: cblPredictionModuleData, 
    isLoading: isLoadingCblPredictionModule,
    error: errorCblPredictionModule,
    refetch: refetchCblPredictionModule
  } = useAiModuleList(cblPredictionModuleParams, {
    enabled: activeTab === 'cbl-module'
  });

  // 탭 변경 감지 - 탭 변경 시 페이지를 1로 리셋하고 refetch
  useEffect(() => {
    const currentTab = searchParams.get('td') || 'learning';
    
    // 탭이 실제로 변경된 경우에만 처리
    if (prevTabRef.current !== currentTab && prevTabRef.current !== '') {
      // 탭 변경 시 페이지를 1로 리셋
      updateSearchParams({ page: '1' });
      
      // 탭 변경 시 해당 탭의 데이터 refetch
      if (currentTab === 'learning') {
        refetchAiLearning();
      } 
      else if (currentTab === 'demand-module') {
        refetchAiModule();
      }
      else if (currentTab === 'cbl-module') {
        refetchCblPredictionModule();
      }
    }
    
    // 이전 탭 값 업데이트
    prevTabRef.current = currentTab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // activeTab 변경만 감지하여 pagination과 충돌 방지

  const handlePageChange = (page) => {
    updateSearchParams({ page: page.toString() });
  };

  const handleRefresh = async () => {
    try {
      setRefreshMsg('...');
      if (activeTab === 'learning') {
        await refetchAiLearning();
        setRefreshMsg(`AI 학습 요청 목록 새로고침 완료!`);
      }
      else if (activeTab === 'demand-module') {
        await refetchAiModule();
        setRefreshMsg(`충전량 예측 AI 모듈 목록 새로고침 완료!`);
      }
      else if (activeTab === 'cbl-module') {
        await refetchCblPredictionModule();
        setRefreshMsg(`CBL 예측 AI 모듈 목록 새로고침 완료!`);
      }
    } catch (error) {
      console.error('목록 새로고침 실패:', error);
      setRefreshMsg('목록 새로고침 실패!');
    } finally {
      setTimeout(() => {
        setRefreshMsg('');
      }, 1500);
    }
  };

  const onDeleteSuccess = () => {
    setTarget(null);
    setIsModuleDeleteModalOpen(false);
    refetchAiModule(aiModuleParams);
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    // UTC 시간 문자열을 UTC로 파싱한 후 KST(Asia/Seoul)로 변환
    return dayjs(dateTimeString).format('YYYY-MM-DD');
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      'Pending': '대기중',
      'InProgress': '진행중',
      'Completed': '완료',
      'Failed': '실패',
      'Cancelled': '취소됨',
      'WaitingForDemand': 'Demand 모델 대기 중'
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Completed': return 'success';
      case 'InProgress': return 'warning';
      case 'Failed': return 'error';
      case 'Pending': return 'unknown';
      case 'WaitingForDemand': return 'warning';
      case 'Cancelled': return 'error';
      default: return 'unknown';
    }
  };

  const aiModelTypeConvert = (aiModelType) => {
    switch (aiModelType) {
      case 'ChargingDemandPrediction': return '충전량 예측 AI 모델';
      case 'CblPrediction': return 'CBL 예측 AI 모델';
      default: return aiModelType;
    }
  }

  const deleteAiModule = useDeleteAiModule();

  const totalPagesLearning = aiLearningData?.Total 
    ? Math.ceil(aiLearningData.Total / pageSize) 
    : 1;

  const totalPagesModule = aiModuleData?.Total 
    ? Math.ceil(aiModuleData.Total / pageSize) 
    : 1;

  const totalPagesCblPredictionModule = cblPredictionModuleData?.Total 
    ? Math.ceil(cblPredictionModuleData.Total / pageSize) 
    : 1;


  const handleKeywordChange = (e) => {
    setKeywordInput(e.target.value);
  };

  const handleKeywordKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const value = e.target.value.trim();
      const current = Object.fromEntries(searchParams.entries());
      
      if (value && value.length >= 2) {
        setSearchParams({ ...current, Keyword: value, page: '1' });
      } else {
        // 빈 값이면 Keyword 파라미터 제거
        const { Keyword, ...rest } = current;
        setSearchParams({ ...rest, page: '1' });
      }
    }
  };

  return (
    <div className="ai-module-management-container page-container">
      <div className="ai-module-management-header">
        <div className="tabs grid-cols-3 w-half">
          <button 
            className={`tab tab ${activeTab === 'learning' ? 'active' : ''}`} 
            onClick={() => setSearchParams({ ...searchParams, td: 'learning' })}
          >
            AI 학습 요청
          </button>
          <button 
            className={`tab tab ${activeTab === 'demand-module' ? 'active' : ''}`} 
            onClick={() => setSearchParams({ ...searchParams, td: 'demand-module' })}
          >
            충전량 예측 AI 모듈
          </button>
          <button 
            className={`tab tab ${activeTab === 'cbl-module' ? 'active' : ''}`} 
            onClick={() => setSearchParams({ ...searchParams, td: 'cbl-module' })}
          >
            CBL 예측 AI 모듈
          </button>
        </div>
      </div>
      <div className="ai-module-management-content w-full">
        {activeTab === 'learning' && (
          <div className="ai-module-management-request-content">

            <div className='flex justify-between align-center mb-5'>
              <div className='flex w-half'>
                <input type="text" placeholder="키워드 검색 (2자 이상) - 기본알고리즘, Ai모델유형" className="input-primary flex-1 text-b2-400" 
                value={keywordInput}
                onChange={handleKeywordChange}
                onKeyDown={handleKeywordKeyDown}
                />
              </div>
              <div className="flex justify-end align-center gap-5">
                <p className="text-b2-400 text-primary">{refreshMsg}</p>
                <button className="btn big-btn btn-style-5 flex align-center gap-3" onClick={handleRefresh}>
                  <RefreshIcon color='var(--gray-600)'/>
                  <p className="text-b2-400 text-gray-600">새로고침</p>
                </button>
                <button className="btn btn-primary big-btn flex align-center gap-3" onClick={() => setIsRequestModalOpen(true)}>
                  <PlusIcon color='var(--white)'/>
                  신규 학습 요청
                </button>
              </div>  
            </div>

            {isLoadingLearning ? (
              <SkeletonTable rows={5} cols={8} />
            ) : errorLearning ? (
              <div className="error-message">데이터를 불러오는 중 오류가 발생했습니다.</div>
            ) : (
              <>
                <table className="primary-table w-full">
                  <thead>
                    <tr>
                      <th width="40px"></th>
                      <th width="100px"></th>
                      <th width="160px">AI 모델 타입</th>
                      <th>요청일시</th>
                      <th>완료일시</th>
                      <th>Base알고리즘</th>
                      <th>버전</th>
                      <th>학습횟수</th>
                      <th>학습 데이터 범위</th>
                      <th>상태</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiLearningData?.Items?.length > 0 ? (
                      aiLearningData.Items.map((item) => (
                        <AiLearningTableRow
                          key={item.AiLearningRecordSysId}
                          aiLearningRecordSysId={item.AiLearningRecordSysId}
                          baseAlgorithm={item.BaseAlgorithm}
                          IsReLearnRequest={item.IsReLearnRequest}
                          version={item.Version}
                          aiModelType={item.AiModelType}
                          IterationCount={item.IterationCount}
                          startTime={formatDateTime(item.StartDate)}
                          endTime={formatDateTime(item.EndDate)}
                          status={getStatusDisplay(item.Status)}
                          statusColor={getStatusColor(item.Status)}
                          requestedAt={formatDateTime(item.RequestedAt)}
                          completedAt={formatDateTime(item.CompletedAt)}
                          logDetails={item.LogDetails}
                        />
                      ))
                    ) : (
                      <tr>
                        <td colSpan="11" className="text-center">데이터가 없습니다.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="pagination-wrapper">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPagesLearning}
                    onPageChange={handlePageChange}
                  />
                </div>
              </>
            )}
          </div>
        )}
        {activeTab === 'demand-module' && (
          <div className="ai-module-management-module-content w-full">
            <div className='flex justify-between align-center mb-5'>
              <div className='flex w-half'>
                <input 
                  type="text" 
                  placeholder="키워드 검색 (2자 이상) - 모듈이름, 버전, 기본알고리즘" 
                  className="input-primary flex-1 text-b2-400" 
                  value={keywordInput}
                  onChange={handleKeywordChange}
                  onKeyDown={handleKeywordKeyDown}
                />
              </div>
              <div className="flex justify-end align-center gap-5">
                <p className="text-b2-400 text-primary">{refreshMsg}</p>
                <button className="btn big-btn btn-style-5 flex align-center gap-3" onClick={handleRefresh}>
                  <RefreshIcon color='var(--gray-600)'/>
                  <p className="text-b2-400 text-gray-600">새로고침</p>
                </button>
              </div>
            </div>
            {isLoadingModule ? (
              <SkeletonTable rows={5} cols={6} />
            ) : errorModule ? (
              <div className="error-message">데이터를 불러오는 중 오류가 발생했습니다.</div>
            ) : (
              <>
                <div className="ai-module-management-module-table w-full">
                  <table className="primary-table w-full">
                    <thead>
                      <tr>
                        <th>AI 모델 타입</th>
                        <th>모듈명</th>
                        <th>버전</th>
                        <th>Base알고리즘</th>
                        <th>생성일시</th>
                        <th width="130px"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {aiModuleData?.Items?.length > 0 ? (
                        aiModuleData.Items.map((item) => (
                          <tr key={item.AiModuleInfoSysId}>
                            <td>
                              <span className={`status-badge ${getStatusColor(item.AiModelType)}`}>
                                {aiModelTypeConvert(item.AiModelType)}
                              </span>
                            </td>
                            <td>{item.ModuleName || '-'}</td>
                            <td>{item.Version || '-'}</td>
                            <td>{item.OriginAiAlgorithm || '-'}</td>
                            <td>{formatDateTime(item.CreatedDate) || '-'}</td>
                            <td>
                              <div className='flex align-center gap-5'>
                                <button className="btn btn-style-5 small-btn" onClick={() => {
                                  setTarget(item);
                                  setIsDemandReRequestModalOpen(true);
                                }}>
                                  재학습
                                </button>
                                <button className="btn icon-btn" onClick={() => {
                                  setTarget(item);
                                  setIsModuleDeleteModalOpen(true);
                                }} disabled={deleteAiModule.isPending}>
                                  <DeleteIcon color='var(--gray-600)'/>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">데이터가 없습니다.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="pagination-wrapper">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPagesModule}
                    onPageChange={handlePageChange}
                  />
                </div>
              </>
            )}
          </div>
        )}
        {activeTab === 'cbl-module' && (
          <div className="ai-module-management-module-content w-full">
            <div className='flex justify-between align-center mb-5'>
              <div className='flex w-half'>
                <input 
                  type="text" 
                  placeholder="키워드 검색 (2자 이상) - 모듈이름, 버전, 기본알고리즘" 
                  className="input-primary flex-1 text-b2-400" 
                  value={keywordInput}
                  onChange={handleKeywordChange}
                  onKeyDown={handleKeywordKeyDown}
                />
              </div>
              <div className="flex justify-end align-center gap-5">
                <p className="text-b2-400 text-primary">{refreshMsg}</p>
                <button className="btn big-btn btn-style-5 flex align-center gap-3" onClick={handleRefresh}>
                  <RefreshIcon color='var(--gray-600)'/>
                  <p className="text-b2-400 text-gray-600">새로고침</p>
                </button>
                <button className="btn btn-primary big-btn flex align-center gap-3" onClick={() => setIsCblReRequestModalOpen(true)}>재학습 요청</button>
              </div>
            </div>
            {isLoadingCblPredictionModule ? (
              <SkeletonTable rows={5} cols={6} />
            ) : errorCblPredictionModule ? (
              <div className="error-message">데이터를 불러오는 중 오류가 발생했습니다.</div>
            ) : (
              <>
                <div className="ai-module-management-module-table w-full">
                  <table className="primary-table w-full">
                    <thead>
                      <tr>
                        <th>AI 모델 타입</th>
                        <th>모듈명</th>
                        <th>버전</th>
                        <th>Base알고리즘</th>
                        <th>생성일시</th>
                        <th width="130px"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {cblPredictionModuleData?.Items?.length > 0 ? (
                        cblPredictionModuleData.Items.map((item) => (
                          <tr key={item.AiModuleInfoSysId}>
                            <td>
                              <span className={`status-badge ${getStatusColor(item.AiModelType)}`}>
                                {aiModelTypeConvert(item.AiModelType)}
                              </span>
                            </td>
                            <td>{item.ModuleName || '-'}</td>
                            <td>{item.Version || '-'}</td>
                            <td>{item.OriginAiAlgorithm || '-'}</td>
                            <td>{formatDateTime(item.CreatedDate) || '-'}</td>
                            <td>
                              <button className="btn icon-btn" onClick={() => {
                                setTarget(item);
                                setIsModuleDeleteModalOpen(true);
                              }} disabled={deleteAiModule.isPending}>
                                <DeleteIcon color='var(--gray-600)'/>
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center">데이터가 없습니다.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="pagination-wrapper">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPagesCblPredictionModule}
                  onPageChange={handlePageChange}
                />
                </div>
              </>
            )}
          </div>
        )}
      </div>
      {isRequestModalOpen && (
        <AiLearningRequestModal
          isOpen={isRequestModalOpen}
          onClose={() => setIsRequestModalOpen(false)}
          onSuccess={() => {
            // 학습 요청 목록 새로고침
            refetchAiLearning();
          }}
        />
      )}
      {isDemandReRequestModalOpen && (
        <AiLearningDemandReRequestModal
          target={target}
          onClose={() => setIsDemandReRequestModalOpen(false)}
          onSuccess={() => {
            // 충전량 예측 모듈 재학습 요청 목록 새로고침
            refetchAiModule();
          }}
        />
      )}
      {isCblReRequestModalOpen && (
        <AiLearningCblReRequestModal
          onClose={() => setIsCblReRequestModalOpen(false)}
          onSuccess={() => {
            // CBL 모듈 재학습 요청 목록 새로고침
            refetchCblPredictionModule();
          }}
        />
      )}
      {isModuleDeleteModalOpen && (
        <AiModuleDeleteModal
          onClose={() => setIsModuleDeleteModalOpen(false)}
          onDeleteSuccess={onDeleteSuccess}
          target={target}
        />
      )}
    </div>
  );
};

export default AiModuleManagement;

